import { useMutationState } from '@tanstack/react-query';
import { mutationState } from '../ui/QueryStates';

/**
 * What the checklist looks like once the writes still sitting on the phone are counted.
 *
 * The defect this exists for: a tech in a crawlspace ticks the last required step, the row
 * shows ticked, and the Finish button stays dead reading *"1 required step left"* — forever,
 * because `requiredOpen` is computed by the server and the server has not heard about the
 * tick. The stop cannot be finished indoors at all.
 *
 * **The fix is a derivation, not a cache write, and that is a decision — not an oversight.**
 * The mutation queue is already the record of "written but not sent". Folding it over the
 * server's answer at read time means a rejected replay puts the count back for free: the
 * mutation leaves the queue and the next render is correct again. The canonical `onMutate`
 * + rollback would instead fire `onError` on replay *hours later*, silently un-ticking a
 * step the tech ticked that morning at a door they have long since left. So after this the
 * app still has **no `setQueryData` anywhere**, and a later reader must not "finish the job"
 * by adding one.
 *
 * ⚠️ The ruling sketched this as `server.requiredOpen - countOfQueuedTicks`. That subtraction
 * is wrong in three reachable cases and this does not use it: an UNTICK has to push the count
 * back UP, ticking the same box twice must not decrement twice, and a note-only write is not a
 * tick at all. Resolving each item to its own latest intent handles all three, and costs the
 * same.
 */

/** One item's state as of the queue: `null` where nothing is waiting to be sent. */
export type UnsentTick = { itemId: string; done: boolean };

type Item = {
  id: string;
  label: string;
  required: boolean;
  note: string | null;
  completedAt: string | Date | null;
};
type Group = {
  jobChecklistId: string;
  name: string;
  items: Item[];
  requiredOpen: number;
};

export type DerivedItem = Item & {
  /** `true`/`false` if a tick for this item is waiting to send; `null` if none is. */
  unsentDone: boolean | null;
};
export type DerivedGroup = Omit<Group, 'items'> & { items: DerivedItem[] };

/**
 * Fold unsent ticks over the server's groups.
 *
 * `requiredOpen` is RECOMPUTED per group rather than adjusted, so the number the button reads
 * and the number the warning prints are the same number by construction — they were two
 * readings of one server field before, and this is the point where they could quietly stop
 * agreeing.
 *
 * Ticks are applied in queue order, so the last one for an item wins: tick-then-untick ends
 * unticked, which is what the tech did.
 */
export function withUnsentTicks(groups: Group[], ticks: UnsentTick[]): DerivedGroup[] {
  if (ticks.length === 0) {
    // Not merely a fast path: it keeps the common case referentially identical to what the
    // server sent, so nothing downstream re-renders because a derivation ran.
    return groups.map((g) => ({
      ...g,
      items: g.items.map((i) => ({ ...i, unsentDone: null })),
    }));
  }

  const intent = new Map<string, boolean>();
  for (const tick of ticks) intent.set(tick.itemId, tick.done);

  return groups.map((group) => {
    const items = group.items.map((item) => ({
      ...item,
      // `?? null` and not `?? undefined`: "no unsent tick" has to be a value the row can
      // fall through, and `undefined` reads as "not computed yet" everywhere else here.
      unsentDone: intent.has(item.id) ? intent.get(item.id)! : null,
    }));
    const requiredOpen = items.filter(
      (i) => i.required && !(i.unsentDone ?? Boolean(i.completedAt)),
    ).length;
    return { ...group, items, requiredOpen };
  });
}

/**
 * Is this mutation an unsent tick for this visit — and if so, which one?
 *
 * Predicate and mapper in one function on purpose: they are the same rule, and splitting
 * them is how a filter and a selector end up disagreeing about what counts.
 *
 * `queued | sending`, not just `queued`: a write genuinely on the wire has not landed
 * either, and treating those two differently is what made this surface lie in the first
 * place. `mutationState` is Wave 0.2's shared classifier, and the trap it exists for — a
 * paused mutation is ALSO pending — is exactly the one that bites here.
 */
export function unsentTickFor(
  visitId: string,
  state: {
    isPaused: boolean;
    status: 'idle' | 'pending' | 'error' | 'success';
    variables?: unknown;
  },
): UnsentTick | null {
  const phase = mutationState(state);
  if (phase !== 'queued' && phase !== 'sending') return null;
  const vars = state.variables as
    | { visitId?: unknown; itemId?: unknown; done?: unknown }
    | undefined;
  if (vars?.visitId !== visitId) return null;
  if (typeof vars.itemId !== 'string') return null;
  // A note-only write carries no `done` and is NOT a tick. Without this it resolves to
  // `undefined` and reads as an untick — typing a reading would silently un-tick the box.
  if (typeof vars.done !== 'boolean') return null;
  return { itemId: vars.itemId, done: vars.done };
}

/**
 * The unsent ticks for one visit, straight off the mutation cache.
 *
 * Filtered on `visitId`, which `checklist.setItem` now carries precisely so a queued write
 * can be read without asking the server anything. Items are then matched by `itemId` in
 * `withUnsentTicks`, which is stricter than the visit filter alone — a tick for an item this
 * screen no longer shows is ignored rather than counted against a group it is not in.
 */
export function useUnsentTicks(visitId: string): UnsentTick[] {
  return useMutationState({
    filters: {
      mutationKey: [['checklist', 'setItem']],
      predicate: (m) => unsentTickFor(visitId, m.state) !== null,
    },
    select: (m) => unsentTickFor(visitId, m.state)!,
  });
}
